import { html, nothing } from "lit";
import type { CSSResultGroup, TemplateResult } from "lit";

import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";

import { autocompleteMultiSelectStyles } from "./autocomplete-multi-select.css";

import "../field-wrapper/field-wrapper";
import { FormElement } from "../form-element";

export type Option = {
  value: string;
  label: string;
  chipLabel?: string;
  startIcon?: TemplateResult;
  endIcon?: TemplateResult;
};

// Re-export for backward compatibility
export type { Option as SelectOption };

@customElement("wow-autocomplete-multi-select")
export class WowAutocompleteMultiSelect extends FormElement {
  static override styles: CSSResultGroup = [autocompleteMultiSelectStyles];

  @property({ type: String }) placeholder = "Search and select...";

  @property({ type: Array }) options: Option[] = [];
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ attribute: "loading-text" }) loadingText = "Loading...";
  @property({ attribute: "no-results-text" }) noResultsText = "No results found";
  @property({ attribute: "empty-search-text" }) emptySearchText = "Start typing to see the options";

  @property({ type: Array }) selectedValues: string[] = [];
  @property({ type: String }) searchTerm = "";
  @state() private isOpen = false;
  @state() private highlightedIndex = -1;

  @query(".search-input") private searchInput!: HTMLInputElement;
  @query(".dropdown-menu") private dropdownMenu!: HTMLElement;
  @query(".autocomplete-field") private fieldElement!: HTMLElement;

  private _cleanup?: () => void;

  // Form integration following wow-input pattern
  @property({ type: String })
  override get value(): string {
    return JSON.stringify(this.selectedValues);
  }

  override set value(newValue: string) {
    try {
      const parsed = JSON.parse(newValue || "[]");
      this.selectedValues =
        Array.isArray(parsed) && parsed.every((item) => typeof item === "string") ? (parsed as string[]) : [];
      this._internals.setFormValue(newValue);
    } catch {
      this.selectedValues = [];
      this._internals.setFormValue("[]");
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    // Close dropdown when clicking outside
    document.addEventListener("click", this.handleDocumentClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleDocumentClick);
    this._cleanup?.();
  }

  private handleDocumentClick = (event: Event) => {
    if (!this.contains(event.target as Node)) {
      this.closeDropdown();
    }
  };

  private get availableOptions(): Option[] {
    return this.options.filter((option) => !this.selectedValues.includes(option.value));
  }

  private _setupPositioning() {
    if (!this.fieldElement || !this.dropdownMenu) return;

    this._cleanup = autoUpdate(this.fieldElement, this.dropdownMenu, () => {
      computePosition(this.fieldElement, this.dropdownMenu, {
        placement: "bottom-start",
        middleware: [
          offset(12), // 12px below the field
          flip(),
          shift({ padding: 8 }),
        ],
      }).then(({ x, y }) => {
        Object.assign(this.dropdownMenu.style, {
          left: `${x}px`,
          top: `${y}px`,
          width: `${this.fieldElement.offsetWidth}px`,
        });
      });
    });
  }

  private openDropdown() {
    if (this.disabled) return;

    this.isOpen = true;
    this.highlightedIndex = this.availableOptions.length > 0 ? 0 : -1;

    // Setup auto-updating position after render
    this.updateComplete.then(() => {
      this._setupPositioning();
    });
  }

  private closeDropdown() {
    this.isOpen = false;
    this.highlightedIndex = -1;
    this._cleanup?.();

    // Dispatch clear search event
    this.dispatchEvent(
      new CustomEvent("search", {
        bubbles: true,
        detail: { searchTerm: "" },
      }),
    );
  }

  private selectOptionInternal(option: Option) {
    if (!this.selectedValues.includes(option.value)) {
      const newValues = [...this.selectedValues, option.value];

      // Dispatch clear search event
      this.dispatchEvent(
        new CustomEvent("search", {
          bubbles: true,
          detail: { searchTerm: "" },
        }),
      );

      // Dispatch change event with new values
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            value: newValues,
          },
        }),
      );
    }
  }

  private removeOption(optionValue: string) {
    const newValues = this.selectedValues.filter((val) => val !== optionValue);

    // Dispatch change event with new values
    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: {
          value: newValues,
        },
      }),
    );
  }

  private handleSearchInput = (event: Event) => {
    const input = event.target as HTMLInputElement;

    // Emit search event for parent components to handle async search
    this.dispatchEvent(
      new CustomEvent("search", {
        bubbles: true,
        detail: { searchTerm: input.value },
      }),
    );

    if (!this.isOpen) {
      this.openDropdown();
    }
  };

  private handleSearchKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else if (this.highlightedIndex < this.availableOptions.length - 1) {
          this.highlightedIndex++;
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
        }
        break;

      case "Enter": {
        event.preventDefault();

        if (this.isOpen) {
          const currentOption = this.availableOptions[this.highlightedIndex];
          if (currentOption) {
            this.selectOptionInternal(currentOption);
          }
        }
        break;
      }

      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        break;

      case "Backspace": {
        // Remove last selected item when input is empty
        const lastValue = this.selectedValues[this.selectedValues.length - 1];

        if (this.searchTerm === "" && lastValue) {
          this.removeOption(lastValue);
        }
        break;
      }

      case "Tab":
        this.closeDropdown();
        break;
    }
  };

  private handleFieldClick = () => {
    if (!this.isOpen) {
      this.openDropdown();
    }
    // Focus the search input
    if (this.searchInput) {
      this.searchInput.focus();
    }
  };

  private handleOptionClick = (option: Option) => {
    this.selectOptionInternal(option);
  };

  private validateInput(): void {
    const isValid = !this.required || this.selectedValues.length > 0;
    this.hasError = !isValid;

    if (!isValid && this.required) {
      const message = "Please select at least one option";
      this._internals.setValidity({ valueMissing: true }, message);
    } else {
      this._internals.setValidity({});
    }
  }

  private handleFocus(): void {
    this.focused = true;
  }

  private handleBlur = (event: FocusEvent): void => {
    const relatedTarget = event.relatedTarget as Node | null;

    // Don't close if focus moved to dropdown menu or its children
    if (relatedTarget && this.dropdownMenu?.contains(relatedTarget)) {
      return;
    }

    // Close dropdown after a small delay to allow for click events
    setTimeout(() => {
      this.focused = false;
      this.validateInput();
      this.closeDropdown();
    }, 100);
  };

  override render() {
    const fieldClasses = {
      "autocomplete-field": true,
      focused: this.focused,
      "has-error": this.hasError,
      disabled: this.disabled,
      open: this.isOpen,
    };

    return html`
      <wow-field-wrapper>
        <slot name="label" slot="label"></slot>
        <slot name="help" slot="help"></slot>

        <div slot="control" class=${classMap(fieldClasses)} @click=${this.handleFieldClick}>
          <!-- Selected option chips/tags -->
          <div class="selected-options">
            ${this.selectedValues.map((value) => {
              const option = this.options.find((opt) => opt.value === value);
              return html`
                <span class="option-chip">
                  ${option?.chipLabel ?? option?.label ?? value}
                  <button
                    type="button"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      this.removeOption(value);
                    }}
                    ?disabled=${this.disabled}
                    aria-label="Remove ${option?.chipLabel ?? option?.label ?? value}"
                  >
                    ×
                  </button>
                </span>
              `;
            })}
          </div>

          <!-- Search input -->
          <input
            class="search-input"
            type="text"
            .value=${this.searchTerm}
            placeholder=${this.placeholder}
            @input=${this.handleSearchInput}
            @keydown=${this.handleSearchKeyDown}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            ?disabled=${this.disabled}
          />

          <!-- Dropdown arrow -->
          <div class="dropdown-arrow">
            <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor">
              <path
                d="M1 1L6 6L11 1"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>

        ${this.validationMessage
          ? html`<span class="error-message" slot="error">${this.validationMessage}</span>`
          : nothing}
      </wow-field-wrapper>

      <!-- Floating dropdown menu -->
      ${this.isOpen
        ? html`
            <div class="dropdown-menu">
              ${this.loading
                ? html`<div class="loading-message">${this.loadingText}</div>`
                : this.searchTerm === "" && this.availableOptions.length === 0
                  ? html`<div class="empty-search-message">${this.emptySearchText}</div>`
                  : this.availableOptions.length === 0
                    ? html`<div class="no-options">${this.noResultsText}</div>`
                    : this.availableOptions.map(
                        (option, index) => html`
                          <div
                            class="menu-item ${index === this.highlightedIndex ? "highlighted" : ""}"
                            @click=${() => this.handleOptionClick(option)}
                            @mouseenter=${() => (this.highlightedIndex = index)}
                          >
                            ${option.startIcon ? html`<span class="start-icon">${option.startIcon}</span>` : nothing}
                            <span class="option-label">${option.label}</span>
                            ${option.endIcon ? html`<span class="end-icon">${option.endIcon}</span>` : nothing}
                          </div>
                        `,
                      )}
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-autocomplete-multi-select": WowAutocompleteMultiSelect;
  }
}
